import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Loader2, ArrowLeft, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", phone: "", lat: null, lng: null, city: "" };

export default function PaintCustomers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [addrSeed, setAddrSeed] = useState(null);

  const load = () => api.get("/paint/customers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing === "new") await api.post("/paint/customers", form);
      else await api.patch(`/paint/customers/${editing}`, form);
      toast.success("Saved");
      setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await api.delete(`/paint/customers/${c.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!rows) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const editorRow = (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-kilted-gold/50 bg-kilted-gold/5 p-3" data-testid="paint-customer-editor">
      <div className="w-full space-y-1 sm:w-auto sm:flex-1"><p className="text-xs font-semibold">Name</p><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="paint-customer-name" /></div>
      <div className="w-full space-y-1 sm:w-auto sm:flex-[2]">
        <AddressAutocomplete label="Address" testid="paint-customer-address" placeholder="Start typing the street or business…"
          value={addrSeed}
          onQueryChange={(q) => setForm((f) => ({ ...f, address: q, lat: null, lng: null, city: "" }))}
          onSelect={(a) => setForm((f) => ({ ...f, address: a.label || "", lat: a.lat ?? null, lng: a.lng ?? null, city: a.city || "" }))} />
      </div>
      <div className="min-w-[120px] flex-1 space-y-1 sm:flex-none"><p className="text-xs font-semibold">Phone</p><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="paint-customer-phone" /></div>
      <Button size="sm" onClick={save} data-testid="paint-customer-save" className="gap-1 bg-kilted-green hover:bg-kilted-greenLight"><Check className="h-3.5 w-3.5" /> Save</Button>
      <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="paint-customer-cancel"><X className="h-3.5 w-3.5" /></Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/paint")} data-testid="paint-customers-back" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to orders</Button>
        <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-kilted-green sm:text-3xl" data-testid="paint-customers-heading">Delivery customers</h1>
        <p className="mt-1 text-sm text-muted-foreground">The people and businesses you regularly send paint to.</p>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Saved customers ({rows.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rows.map((c) => editing === c.id ? <div key={c.id}>{editorRow}</div> : (
              <div key={c.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid={`paint-customer-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{c.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.address}{c.phone ? ` · ${c.phone}` : ""}</p>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(c.id); setForm({ name: c.name, address: c.address || "", phone: c.phone || "", lat: c.lat ?? null, lng: c.lng ?? null, city: c.city || "" }); setAddrSeed(c.address ? { label: c.address, lat: c.lat, lng: c.lng, city: c.city } : null); }} data-testid={`paint-customer-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(c)} data-testid={`paint-customer-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            {editing === "new" ? editorRow : (
              <Button variant="outline" size="sm" onClick={() => { setEditing("new"); setForm(EMPTY); setAddrSeed(null); }} data-testid="paint-customer-add" className="gap-1.5 border-kilted-green text-kilted-green"><Plus className="h-3.5 w-3.5" /> Add customer</Button>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
